import type { CombinedError, Operation } from 'urql';

import type { Ory } from '@revelio/auth';
import {
  isWarningError,
  KIBANA_LOG_LEVEL,
  type LogBody,
} from '@revelio/data-access';

export type LogOptions = Omit<LogBody, 'message' | 'level' | 'source'>;

export class KibanaLogger {
  private source;
  public getAuthInfo;
  private dashMetaRoot;
  public sendLogs;
  private version?: string;

  constructor({
    source = 'REVELIO_DASHBOARD',
    getAuthInfo,
    dashMetaRoot,
    sendLogs = true,
    version,
  }: {
    source: string;
    getAuthInfo: () => {
      dashMetaCsrfToken?: string;
      ory?: Partial<Ory>;
    };
    dashMetaRoot: string;
    sendLogs?: boolean;
    version?: string;
  }) {
    this.source = source;
    this.getAuthInfo = getAuthInfo;
    this.dashMetaRoot = dashMetaRoot;
    this.sendLogs = sendLogs;
    this.version = version;
  }

  public setSendLogs(status: boolean) {
    this.sendLogs = status;
  }

  public log = async (message: string, logOptions?: LogOptions) => {
    const logBody: LogBody = {
      source: this.source,
      level: KIBANA_LOG_LEVEL.INFO,
      message,
      version: this.version,
      username: this.getAuthInfo()?.ory?.username,
      'request-id': crypto.randomUUID(),
      ...logOptions,
    };
    return this.sendLog(logBody);
  };

  public warn = async (message: string, logOptions?: LogOptions) => {
    const logBody: LogBody = {
      source: this.source,
      level: KIBANA_LOG_LEVEL.WARN,
      message,
      version: this.version,
      username: this.getAuthInfo()?.ory?.username,
      'request-id': crypto.randomUUID(),
      ...logOptions,
    };

    return this.sendLog(logBody);
  };

  public error = async (message: string, logOptions?: LogOptions) => {
    const logBody: LogBody = {
      source: this.source,
      level: KIBANA_LOG_LEVEL.ERROR,
      message,
      version: this.version,
      username: this.getAuthInfo()?.ory?.username,
      'request-id': crypto.randomUUID(),
      ...logOptions,
    };
    return this.sendLog(logBody);
  };

  private sendLog = async (logBody: LogBody) => {
    if (!this.sendLogs) {
      console.warn('Sending logs is turned off');
      return { message: 'Sending logs is turned off' };
    }

    const csrfToken = this.getAuthInfo()?.dashMetaCsrfToken;
    if (!csrfToken) {
      console.warn('Missing CSRF token');
      return { message: 'Missing CSRF token' };
    }

    const sendLogResponse = await fetch(`${this.dashMetaRoot}/api/log`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken,
      },
      body: JSON.stringify(logBody),
    });
    return await sendLogResponse.json();
  };

  public getUrqlOperationLog = (operation: Operation) => {
    const fetchOptions = operation?.context?.fetchOptions;
    const headers = ((): { [key: string]: string } | undefined => {
      if (fetchOptions instanceof Function) {
        return fetchOptions()?.headers as { [key: string]: string } | undefined;
      } else {
        return fetchOptions?.headers as { [key: string]: string } | undefined;
      }
    })();

    const requestId = headers?.['x-request-id'];

    const oryUser = this.getAuthInfo()?.ory;
    const username = oryUser?.username || '';

    const query = operation.query?.loc?.source?.body
      ?.replace(/\n/g, '')
      ?.replace(/\s\s+/g, ' ');
    const variables = JSON.stringify(operation.variables);

    const message = `query: ${query}\n\nvariables:${variables}`;

    const logOptions: LogOptions = {
      'request-id': requestId,
      url: operation?.context?.url,
      username,
    };

    return { message, logOptions };
  };

  public getUrqlErrorLog = (error: CombinedError, operation: Operation) => {
    const { message, logOptions } = this.getUrqlOperationLog(operation);

    const response = error?.response as Response | undefined;
    const status = response?.status;

    const responseHeaders = response?.headers;
    const correlationId = responseHeaders?.get('X-Correlation-Id') || undefined;

    const errorMessage = `[${status}] ${error.message}\n\n${message}`;

    const errorLogOptions: LogOptions = {
      ...logOptions,
      'correlation-id': correlationId,
    };

    return {
      message: errorMessage,
      logOptions: errorLogOptions,
      isWarningLevel: isWarningError(error),
    };
  };
}
